import { RoleBadge } from "@/components/admin-components/RoleBadge";
import { StatusBadge } from "@/components/admin-components/StatusBadge";
import { ErrorState } from "@/components/ErrorState";
import Loader from "@/components/loader/Loader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSingleUser, useUserTransactions } from "@/hooks";
import type { UserRole, UserStatus } from "@/interfaces/profile";
import { copyToClipboard } from "@/utils";
import { formatCurrency } from "@/utils/formatCurrency";
import {
  Activity,
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  CheckCircle2,
  Clock3,
  Copy,
  Database,
  ExternalLink,
  Eye,
  Shield,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const AdminUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: user, isLoading, isError, refetch } = useSingleUser(id!);
  const { data: transactionData } = useUserTransactions(id!);

  console.log("USER", user);

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value?: React.ReactNode;
  }) => (
    <div className="flex items-center justify-between gap-4 py-2 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>

      <span className="min-w-0 text-right font-medium text-foreground">
        {value ?? "N/A"}
      </span>
    </div>
  );

  const InfoSection = ({
    title,
    icon,
    children,
  }: {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
  }) => (
    <div className="rounded-xl border bg-card p-4">
      <div className="mb-4 flex items-center gap-2">
        <Badge variant="outline" className="gap-2 px-3 py-1.5">
          {title}
          {icon}
        </Badge>
      </div>

      <div className="divide-y">{children}</div>
    </div>
  );

  const transactions = transactionData?.data ?? [];

  const formatTransactionAmount = (transaction: any) => {
    const amount = Number(transaction.amount ?? 0);

    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 8,
    }).format(amount);
  };

  const formatAccountingAmount = (transaction: any) => {
    const amount = Number(transaction.accountingAmount ?? 0);
    const currency = transaction.accountingCurrency ?? "USD";

    return `${currency} ${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)}`;
  };

  const formatTransactionDate = (date?: string) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const shortenHash = (hash?: string | null) => {
    if (!hash) return "N/A";

    return `${hash.slice(0, 8)}...${hash.slice(-8)}`;
  };

  const getTransactionLabel = (transaction: any) => {
    switch (transaction.action) {
      case "DEPOSIT":
        return "Deposit";

      case "WITHDRAWAL":
        return "Withdrawal";

      case "TRANSFER":
        return "Transfer";

      case "SWAP":
        return "Swap";

      default:
        return transaction.action || transaction.type || "Transaction";
    }
  };

  const getTransactionIcon = (transaction: any) => {
    switch (transaction.action) {
      case "DEPOSIT":
        return <ArrowDownLeft className="h-4 w-4" />;

      case "WITHDRAWAL":
        return <ArrowUpRight className="h-4 w-4" />;

      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getTransactionStatusIcon = (status?: string) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle2 className="h-3.5 w-3.5" />;

      case "FAILED":
      case "CANCELLED":
        return <XCircle className="h-3.5 w-3.5" />;

      case "PENDING":
      case "PROCESSING":
        return <Clock3 className="h-3.5 w-3.5" />;

      default:
        return <Activity className="h-3.5 w-3.5" />;
    }
  };

  const TransactionStatusBadge = ({ status }: { status?: string }) => {
    const normalized = status?.toUpperCase();

    const classes =
      normalized === "COMPLETED"
        ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
        : normalized === "FAILED" || normalized === "CANCELLED"
          ? "bg-red-500/10 text-red-600 border-red-500/20"
          : normalized === "PENDING" || normalized === "PROCESSING"
            ? "bg-amber-500/10 text-amber-700 border-amber-500/20"
            : "bg-muted text-muted-foreground border-border";

    return (
      <Badge
        variant="outline"
        className={`inline-flex items-center gap-1.5 ${classes}`}
      >
        {getTransactionStatusIcon(normalized)}
        {normalized || "UNKNOWN"}
      </Badge>
    );
  };

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="p-2">
      <div className="space-y-6">
        <Button
          variant="ghost"
          className="hidden sm:flex"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between gap-4">
          <span className="text-2xl">User details</span>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/users/edit/${id}`)}
            >
              Edit User
            </Button>
          </div>
        </div>
        {/* ================= PROFILE HEADER ================= */}
        <div className="rounded-xl border bg-card p-5">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            {/* Left Section */}
            <div className="rounded-2xl bg-card shadow-sm">
              <div className="p-5 sm:p-6">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex min-w-0 flex-col items-center gap-4 sm:flex-row sm:items-start">
                    {/* Avatar */}
                    <div className="relative shrink-0">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-background bg-gradient-to-br from-primary to-primary/70 text-lg font-bold uppercase text-black shadow-sm ring-1 ring-border">
                        {`${user?.firstName?.[0] || ""}${
                          user?.lastName?.[0] || ""
                        }`.toUpperCase() || "U"}
                      </div>

                      {/* Online / active indicator */}
                      <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-card bg-emerald-500" />
                    </div>

                    {/* User Information */}
                    <div className="min-w-0 w-full text-center sm:text-left">
                      {/* Name */}
                      <div className="flex min-w-0 items-center justify-center gap-2 sm:justify-start">
                        <h2 className="truncate text-lg font-semibold tracking-tight">
                          {user?.firstName}{" "}
                          {user?.middleName ? `${user.middleName} ` : ""}
                          {user?.lastName}
                        </h2>
                      </div>

                      {/* Email */}
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">
                        {user?.email || "No email available"}
                      </p>

                      {/* Country */}
                      {user?.country?.name && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {user.country.name}
                        </p>
                      )}

                      <div className="mt-4 flex w-full min-w-0 items-center gap-2 overflow-x-auto">
                        {/* UID */}
                        <div className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border bg-muted/40 px-3">
                          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            UID
                          </span>

                          <span className="font-mono text-xs font-semibold text-foreground">
                            {user?.uid || "N/A"}
                          </span>
                        </div>

                        {/* Wallet */}
                        <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border bg-muted/40 px-3">
                          <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            Wallet
                          </span>

                          <span
                            className="min-w-0 flex-1 truncate font-mono text-xs font-medium text-foreground"
                            title={user?.walletAddresses?.[0]?.address || "N/A"}
                          >
                            {user?.walletAddresses?.[0]?.address
                              ? `${user.walletAddresses[0].address.slice(
                                  0,
                                  8,
                                )}...${user.walletAddresses[0].address.slice(-6)}`
                              : "N/A"}
                          </span>

                          {user?.walletAddresses?.[0]?.address && (
                            <button
                              type="button"
                              aria-label="Copy wallet address"
                              title="Copy wallet address"
                              onClick={async (e) => {
                                e.stopPropagation();

                                const address = user.walletAddresses[0].address;

                                if (!address) return;

                                try {
                                  await copyToClipboard(address);

                                  toast.success("Wallet address copied", {
                                    description:
                                      "The wallet address has been copied to your clipboard.",
                                  });
                                } catch {
                                  toast.error("Unable to copy wallet address", {
                                    description: "Please try again.",
                                  });
                                }
                              }}
                              className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                            >
                              <Copy className="size-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section */}
            <div className="grid w-full grid-cols-2 gap-3 lg:w-auto lg:min-w-[220px]">
              {/* Status */}
              <div className="rounded-xl border bg-muted/20 p-3">
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Account Status
                </p>

                <StatusBadge status={user?.status as UserStatus} />
              </div>

              {/* Role */}
              <div className="rounded-xl border bg-muted/20 p-3">
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Account Role
                </p>

                <RoleBadge role={user?.role as UserRole} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">Wallet Balance</p>

            <p className="mt-1 text-xl font-semibold">
              {user?.wallet?.totalBalanceSymbol || "$"}{" "}
              {formatCurrency(user?.wallet?.totalBalance) ?? "0.00"}{" "}
            </p>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">Trade Volume</p>

            <p className="mt-1 text-xl font-semibold">
              $ {formatCurrency(user?.tradeVolume ?? 0.0).toLocaleString()}
            </p>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">Completed Trades</p>

            <p className="mt-1 text-xl font-semibold">
              {user?.completedTrades ?? 0}
            </p>
          </div>

          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">Reputation</p>

            <p className="mt-1 text-xl font-semibold">
              {user?.reputationScore ?? 0}
            </p>
          </div>
        </div>

        {/* ================= GRID SECTIONS ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoSection
            title="Account Overview"
            icon={<BadgeCheck className="h-4 w-4" />}
          >
            <InfoRow
              label="Account Age"
              value={`${user?.accountAgeDays ?? 0} days`}
            />

            <InfoRow
              label="Status"
              value={<StatusBadge status={user?.status as UserStatus} />}
            />

            <InfoRow
              label="Role"
              value={<RoleBadge role={user?.role as UserRole} />}
            />

            <InfoRow label="KYC Status" value={user?.kycStatus || "N/A"} />

            <InfoRow
              label="Reputation Tier"
              value={user?.reputationTier || "N/A"}
            />

            <InfoRow
              label="Advertiser Tier"
              value={user?.advertiserTier || "N/A"}
            />

            <InfoRow
              label="Moderation"
              value={user?.moderationStatus || "N/A"}
            />

            <InfoRow
              label="Referral Code"
              value={user?.referralCode || "N/A"}
            />
          </InfoSection>

          <InfoSection
            title="Trading Performance"
            icon={<Activity className="h-4 w-4" />}
          >
            <InfoRow
              label="Trade Volume"
              value={`$${formatCurrency(user?.tradeVolume ?? 0.0).toLocaleString()}`}
            />

            <InfoRow
              label="30d Trade Volume"
              value={`$${formatCurrency(user?.tradeVolume30d ?? 0.0).toLocaleString()}`}
            />

            <InfoRow
              label="Completed Trades"
              value={user?.completedTrades ?? 0}
            />

            <InfoRow
              label="Completed Trades (30d)"
              value={user?.completedTrades30d ?? 0}
            />

            <InfoRow
              label="Cancelled Trades"
              value={user?.cancelledTrades ?? 0}
            />

            <InfoRow
              label="Disputed Trades"
              value={user?.disputedTrades ?? 0}
            />

            <InfoRow
              label="Completion Rate"
              value={`${user?.completionRate ?? 0}%`}
            />

            <InfoRow
              label="Payment Success Rate"
              value={`${user?.paymentSuccessRate ?? 0}%`}
            />

            <InfoRow
              label="Dispute Rate"
              value={`${user?.disputeRate ?? 0}%`}
            />

            <InfoRow label="Average Rating" value={user?.averageRating ?? 0} />

            <InfoRow
              label="Average Response"
              value={`${user?.averageResponseTime ?? 0} min`}
            />

            <InfoRow
              label="Average Release"
              value={`${user?.averageReleaseTime ?? 0} min`}
            />

            <InfoRow
              label="Repeat Customers"
              value={user?.repeatCustomers ?? 0}
            />

            <InfoRow
              label="Reputation Score"
              value={user?.reputationScore ?? 0}
            />
          </InfoSection>

          {/* -------- IDENTITY -------- */}
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 p-2">
                Identity <BadgeCheck className="w-5 h-5" />{" "}
              </Badge>
            </h3>

            <InfoRow
              label="Email Verified"
              value={user?.emailVerified ? "Yes" : "No"}
            />
            <InfoRow
              label="Phone Verified"
              value={user?.phoneVerified ? "Yes" : "No"}
            />
            <InfoRow label="Username" value={user?.username || "N/A"} />
            <InfoRow
              label="Merchant Name"
              value={user?.merchantName || "N/A"}
            />
            <InfoRow label="Auth Type" value={user?.authType} />
            <InfoRow
              label="Country"
              value={user?.lastLoginDetails?.location?.country}
            />
            <InfoRow
              label="Region"
              value={user?.lastLoginDetails?.location?.region}
            />
            <InfoRow
              label="City"
              value={user?.lastLoginDetails?.location?.city}
            />
          </div>

          <InfoSection
            title="Wallet Overview"
            icon={<Database className="h-4 w-4" />}
          >
            <InfoRow label="Wallet Type" value={user?.wallet?.type || "N/A"} />

            <InfoRow
              label="Custodial"
              value={user?.wallet?.isCustodial ? "Yes" : "No"}
            />

            <InfoRow
              label="Frozen"
              value={user?.wallet?.isFrozen ? "Yes" : "No"}
            />

            <InfoRow
              label="Total Balance"
              value={`${formatCurrency(user?.wallet?.totalBalance) ?? "0.00"} ${
                user?.wallet?.totalBalanceSymbol || "$"
              }`}
            />

            <InfoRow
              label="Total Inflow"
              value={`${formatCurrency(user?.wallet?.totalInflow) ?? "0.00"} ${
                user?.wallet?.totalInflowSymbol || "$"
              }`}
            />

            <InfoRow
              label="Total Outflow"
              value={`${formatCurrency(user?.wallet?.totalOutflow) ?? "0.00"} ${
                user?.wallet?.totalOutflowSymbol || "$"
              }`}
            />

            <InfoRow
              label="Locked Balance"
              value={`${formatCurrency(user?.wallet?.totalLockedBalance) ?? "0.00"} ${
                user?.wallet?.totalLockedBalanceSymbol || "$"
              }`}
            />

            <InfoRow
              label="Reserved Balance"
              value={`${formatCurrency(user?.wallet?.totalReservedBalance) ?? "0.00"} ${
                user?.wallet?.totalReservedBalanceSymbol || "$"
              }`}
            />
          </InfoSection>

          {/* -------- SECURITY -------- */}
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              <Badge className="bg-red-500/10 text-red-600 border-red-500/20 p-2">
                Security <Shield className="w-5 h-5" />
              </Badge>
            </h3>

            <InfoSection title="Security" icon={<Shield className="h-4 w-4" />}>
              <InfoRow
                label="Email Verified"
                value={user?.emailVerified ? "Yes" : "No"}
              />

              <InfoRow
                label="Phone Verified"
                value={user?.phoneVerified ? "Yes" : "No"}
              />

              <InfoRow
                label="2FA"
                value={user?.twoFactorEnabled ? "Enabled" : "Disabled"}
              />

              <InfoRow
                label="Security Code Verified"
                value={user?.isSecurityCodeVerified ? "Yes" : "No"}
              />

              <InfoRow
                label="Failed Login Attempts"
                value={user?.failedLoginAttempts ?? 0}
              />

              <InfoRow
                label="Security Code Attempts"
                value={user?.securityCodeAttempts ?? 0}
              />

              <InfoRow
                label="Transaction PIN Attempts"
                value={user?.txnPINAttempts ?? 0}
              />

              <InfoRow
                label="Login Locked Until"
                value={
                  user?.loginLockedUntil
                    ? new Date(user.loginLockedUntil).toLocaleString()
                    : "Not locked"
                }
              />

              <InfoRow
                label="Security Code Locked Until"
                value={
                  user?.securityCodeLockedUntil
                    ? new Date(user.securityCodeLockedUntil).toLocaleString()
                    : "Not locked"
                }
              />

              <InfoRow
                label="Transaction PIN Locked Until"
                value={
                  user?.txnPINLockedUntil
                    ? new Date(user.txnPINLockedUntil).toLocaleString()
                    : "Not locked"
                }
              />
            </InfoSection>
          </div>

          <div className="rounded-xl border bg-card">
            <div className="border-b p-4">
              <h3 className="font-semibold">Recent Audit Activity</h3>

              <p className="text-sm text-muted-foreground">
                {user?.auditLogs?.length ?? 0} recorded events
              </p>
            </div>

            <div className="divide-y">
              {user?.auditLogs?.slice(0, 20).map((log: any) => (
                <div key={log.id} className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium">
                      {log.action || log.event || "Activity"}
                    </span>

                    <span className="text-xs text-muted-foreground">
                      {log.createdAt
                        ? new Date(log.createdAt).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>

                  {log.ipAddress && (
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      IP: {log.ipAddress}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <InfoSection
            title="Reviews & Reputation"
            icon={<BadgeCheck className="h-4 w-4" />}
          >
            <InfoRow label="Total Reviews" value={user?.totalReviews ?? 0} />

            <InfoRow label="Average Rating" value={user?.averageRating ?? 0} />

            <InfoRow
              label="Positive Reviews"
              value={`${user?.positiveReviewPercent ?? 0}%`}
            />

            <InfoRow
              label="Negative Reviews"
              value={`${user?.negativeReviewPercent ?? 0}%`}
            />

            <InfoRow
              label="Reviews Received"
              value={user?.reviewsReceived?.length ?? 0}
            />

            <InfoRow
              label="Reviews Written"
              value={user?.reviewsWritten?.length ?? 0}
            />
          </InfoSection>

          <InfoSection
            title="Payment Methods"
            icon={<Database className="h-4 w-4" />}
          >
            {user?.paymentMethods?.length ? (
              user.paymentMethods.map((method: any) => (
                <div key={method.id} className="py-3">
                  <div className="flex justify-between">
                    <span className="font-medium">
                      {method.name || method.type || "Payment Method"}
                    </span>

                    <Badge variant="outline">{method.status || "Active"}</Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-2 text-sm text-muted-foreground">
                No payment methods configured.
              </p>
            )}
          </InfoSection>

          {/* -------- KYC & COMPLIANCE -------- */}
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20 p-2">
                Compliance <ShieldAlert className="w-5 h-5" />
              </Badge>
            </h3>

            <InfoRow label="KYC Level" value={`Level ${user?.kycLevel}`} />
            <InfoRow label="KYC Status" value={user?.kycStatus} />
            <InfoRow label="Advertiser Tier" value={user?.advertiserTier} />
            <InfoRow label="Moderation" value={user?.moderationStatus} />
          </div>

          {/* -------- ACTIVITY -------- */}
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 p-2">
                Activity <Activity className="w-5 h-5" />
              </Badge>
            </h3>

            <InfoRow label="Completed Trades" value={user?.completedTrades} />
            <InfoRow label="Cancelled Trades" value={user?.cancelledTrades} />
            <InfoRow label="Total Reviews" value={user?.totalReviews} />
            <InfoRow label="Reputation Score" value={user?.reputationScore} />
            <InfoRow label="Dispute Rate" value={`${user?.disputeRate}%`} />
          </div>
        </div>

        {/* ================= SYSTEM INFO ================= */}
        <div className="border rounded-lg p-4 space-y-2">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase">
            <Badge className="bg-slate-500/10 text-slate-600 border-slate-500/20 p-2">
              System Metadata <Database className="w-5 h-5" />
            </Badge>
          </h3>

          <InfoRow
            label="Created At"
            value={
              user?.createdAt && new Date(user?.createdAt).toLocaleString()
            }
          />
          <InfoRow
            label="Updated At"
            value={
              user?.updatedAt
                ? new Date(user.updatedAt).toLocaleString()
                : "N/A"
            }
          />
          <InfoRow
            label="Last Seen"
            value={
              user?.lastSeen ? new Date(user.lastSeen).toLocaleString() : "N/A"
            }
          />
        </div>

        {/* ================= TRANSACTION HISTORY ================= */}

        <div className="rounded-xl border bg-card">
          {/* Header */}
          <div className="border-b p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="gap-2 px-3 py-1.5">
                    <Database className="h-4 w-4" />
                    Transaction History
                  </Badge>

                  <span className="text-sm text-muted-foreground">
                    {transactions.length} transactions
                  </span>
                </div>

                <p className="mt-2 text-sm text-muted-foreground">
                  Financial activity associated with this user's account.
                </p>
              </div>

              {transactions.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // navigate(`/admin/transactions`);
                  }}
                >
                  View All
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {transactions.length === 0 ? (
            <div className="flex min-h-[180px] flex-col items-center justify-center p-6 text-center">
              <div className="mb-3 rounded-full bg-muted p-3">
                <Database className="h-5 w-5 text-muted-foreground" />
              </div>

              <p className="font-medium">No transactions found</p>

              <p className="mt-1 text-sm text-muted-foreground">
                This user has no recorded financial transactions.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop / tablet table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[950px] text-sm">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">
                        Transaction
                      </th>

                      <th className="px-4 py-3 text-left font-medium">Type</th>

                      <th className="px-4 py-3 text-right font-medium">
                        Amount
                      </th>

                      <th className="px-4 py-3 text-right font-medium">
                        Value
                      </th>

                      <th className="px-4 py-3 text-left font-medium">
                        Status
                      </th>

                      <th className="px-4 py-3 text-left font-medium">Date</th>

                      <th className="px-4 py-3 text-right font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y">
                    {transactions.slice(0, 20).map((transaction: any) => {
                      const isCredit = transaction.type === "CREDIT";

                      return (
                        <tr
                          key={transaction.id}
                          className="transition-colors hover:bg-muted/20"
                        >
                          {/* Transaction */}
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                  isCredit
                                    ? "bg-emerald-500/10 text-emerald-600"
                                    : "bg-red-500/10 text-red-600"
                                }`}
                              >
                                {getTransactionIcon(transaction)}
                              </div>

                              <div className="min-w-0">
                                <p className="font-medium">
                                  {getTransactionLabel(transaction)}
                                </p>

                                <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                                  {transaction.id.slice(0, 8)}...
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Type */}
                          <td className="px-4 py-4">
                            <Badge variant="outline">
                              {transaction.type || "N/A"}
                            </Badge>
                          </td>

                          {/* Amount */}
                          <td className="px-4 py-4 text-right">
                            <div
                              className={`font-mono font-medium ${
                                isCredit ? "text-emerald-600" : "text-red-600"
                              }`}
                            >
                              {isCredit ? "+" : "-"}
                              {formatTransactionAmount(transaction)}
                            </div>

                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {transaction.asset?.currency ||
                                transaction.asset?.token?.symbol ||
                                transaction.currency ||
                                "Asset"}
                            </p>
                          </td>

                          {/* Accounting value */}
                          <td className="px-4 py-4 text-right">
                            <p className="font-medium">
                              {formatAccountingAmount(transaction)}
                            </p>

                            {transaction.exchangeRate && (
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                Rate: {transaction.exchangeRate}
                              </p>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-4">
                            <TransactionStatusBadge
                              status={transaction.status}
                            />
                          </td>

                          {/* Date */}
                          <td className="px-4 py-4">
                            <div className="whitespace-nowrap">
                              <p className="font-medium">
                                {formatTransactionDate(transaction.createdAt)}
                              </p>

                              {transaction.confirmedAt && (
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  Confirmed{" "}
                                  {formatTransactionDate(
                                    transaction.confirmedAt,
                                  )}
                                </p>
                              )}
                            </div>
                          </td>

                          {/* Action */}
                          <td className="px-4 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="View transaction"
                              onClick={() => {
                                navigate(
                                  `/admin/transactions/${transaction.id}`,
                                );
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile transaction cards */}
              <div className="divide-y md:hidden">
                {transactions.slice(0, 20).map((transaction: any) => {
                  const isCredit = transaction.type === "CREDIT";

                  return (
                    <div
                      key={transaction.id}
                      className="p-4 transition-colors hover:bg-muted/20"
                    >
                      {/* Top */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                              isCredit
                                ? "bg-emerald-500/10 text-emerald-600"
                                : "bg-red-500/10 text-red-600"
                            }`}
                          >
                            {getTransactionIcon(transaction)}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {getTransactionLabel(transaction)}
                            </p>

                            <p className="font-mono text-xs text-muted-foreground">
                              {transaction.id.slice(0, 8)}...
                            </p>
                          </div>
                        </div>

                        <TransactionStatusBadge status={transaction.status} />
                      </div>

                      {/* Amount */}
                      <div className="mt-4 flex items-end justify-between gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Amount
                          </p>

                          <p
                            className={`mt-1 font-mono text-lg font-semibold ${
                              isCredit ? "text-emerald-600" : "text-red-600"
                            }`}
                          >
                            {isCredit ? "+" : "-"}
                            {formatTransactionAmount(transaction)}
                          </p>

                          <p className="text-xs text-muted-foreground">
                            {transaction.asset?.currency ||
                              transaction.asset?.token?.symbol ||
                              transaction.currency ||
                              "Asset"}
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Value</p>

                          <p className="mt-1 font-semibold">
                            {formatAccountingAmount(transaction)}
                          </p>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-muted/30 p-3">
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            Type
                          </p>

                          <p className="mt-1 text-sm font-medium">
                            {transaction.type || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            Action
                          </p>

                          <p className="mt-1 text-sm font-medium">
                            {transaction.action || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            Date
                          </p>

                          <p className="mt-1 text-sm font-medium">
                            {formatTransactionDate(transaction.createdAt)}
                          </p>
                        </div>

                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            Source
                          </p>

                          <p className="mt-1 text-sm font-medium">
                            {transaction.source || "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Hash */}
                      {transaction.txHash && (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              Transaction Hash
                            </p>

                            <p className="truncate font-mono text-xs">
                              {shortenHash(transaction.txHash)}
                            </p>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            title="Copy transaction hash"
                            onClick={async () => {
                              try {
                                await copyToClipboard(transaction.txHash);

                                toast.success("Transaction hash copied");
                              } catch {
                                toast.error("Unable to copy transaction hash");
                              }
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl border bg-card">
          <div className="border-b p-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline">Wallet Assets</Badge>

              <span className="text-sm text-muted-foreground">
                {user?.wallet?.assets?.length ?? 0} assets
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Asset</th>

                  <th className="px-4 py-3 text-right font-medium">
                    Available
                  </th>

                  <th className="px-4 py-3 text-right font-medium">Locked</th>

                  <th className="px-4 py-3 text-right font-medium">Total</th>

                  <th className="px-4 py-3 text-right font-medium">Value</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {user?.wallet?.assets?.map((asset: any) => (
                  <tr key={asset.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {asset.currency || asset.token?.symbol}
                      </div>

                      <div className="text-xs text-muted-foreground">
                        {asset.token?.name || "N/A"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right font-mono">
                      {asset.available}
                    </td>

                    <td className="px-4 py-3 text-right font-mono">
                      {asset.totalLockedBalance}
                    </td>

                    <td className="px-4 py-3 text-right font-mono">
                      {asset.totalBalance}
                    </td>

                    <td className="px-4 py-3 text-right font-medium">
                      {formatCurrency(asset.convertedBalance)}{" "}
                      {asset.convertedBalanceSymbol}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= ACTIONS ================= */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/users/edit/${id}`)}
          >
            Edit User
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminUser;
